import React from "react";
import { FmButton } from "./FmButton";
import { FmChip } from "./FmChip";
import { FmInline, FmStack, FmText } from "./FmComposition";
import { FmEmpty } from "./FmFeedback";
import { FmCellStack } from "./FmListPrimitives";
import { FmRowActions } from "./FmRowActions";
import { MapWorkspaceLegend } from "./MapWorkspaceLegend";
import type {
  FmMapBasemapDescriptor,
  FmMapFilterDescriptor,
  FmMapLayerDescriptor,
  FmMapLayerId,
  FmMapPanelDescriptor,
} from "./FmMapWorkspaceRoot.types";
import { FmResponsive } from "./FmResponsive";
import { FmSelect } from "./FmSelect";
import { FmSwitch } from "./FmSwitch";
import { FmSection } from "./sections/FmSection";

interface PanelProps {
  panel: FmMapPanelDescriptor;
  layers: readonly FmMapLayerDescriptor[];
  baseMaps: readonly FmMapBasemapDescriptor[];
  activeBaseMapId: FmMapBasemapDescriptor["id"];
  filters: readonly FmMapFilterDescriptor[];
  filterValues: Readonly<Record<string, readonly string[]>>;
  onFilterChange: (filterId: string, values: readonly string[]) => void;
  onLayerToggle: (layerId: FmMapLayerId, enabled: boolean) => void;
  onBaseMapSelect: (baseMapId: FmMapBasemapDescriptor["id"]) => void;
  onAction: (actionId: string) => void;
  onCollectionSelect: (panelId: string, recordId: string) => void;
}

const FilterPanelContent: React.FC<PanelProps> = ({
  panel,
  filters,
  filterValues,
  onFilterChange,
}) => {
  if (panel.kind !== "filters") return null;
  return (
    <FmStack gap="md" data-testid="forest-stand-filters">
      <FmButton
        variant="text"
        onClick={() =>
          panel.filterIds.forEach((filterId) => onFilterChange(filterId, []))
        }
        data-testid="filter-reset-btn"
      >
        {panel.resetLabel}
      </FmButton>
      {panel.filterIds.map((filterId) => {
        const filter = filters.find((candidate) => candidate.id === filterId);
        if (!filter) return null;
        const selected = filterValues[filter.id] ?? [];
        if (filter.operator === "boolean") {
          const trueValue = filter.options[0]?.value ?? "true";
          return (
            <FmInline justify="between" gap="sm" key={filter.id}>
              <FmText>{filter.label}</FmText>
              <FmSwitch
                checked={selected.includes(trueValue)}
                onChange={(checked) =>
                  onFilterChange(filter.id, checked ? [trueValue] : [])
                }
                ariaLabel={filter.label}
                data-testid={
                  filter.id === "stand-maturity"
                    ? "mature-only-switch"
                    : undefined
                }
              />
            </FmInline>
          );
        }
        return (
          <FmStack gap="xs" key={filter.id}>
            <FmText size="caption" tone="muted">
              {filter.label}
            </FmText>
            <FmSelect<string[]>
              mode="multiple"
              value={[...selected]}
              options={filter.options.map((option) => ({ ...option }))}
              virtual={false}
              onChange={(values) => onFilterChange(filter.id, values)}
              controlWidth="fill"
              optionFilterProp="label"
              ariaLabel={filter.label}
              data-testid={
                filter.id === "stand-species"
                  ? "species-filter-select"
                  : undefined
              }
            />
          </FmStack>
        );
      })}
    </FmStack>
  );
};

const FilterPanel: React.FC<PanelProps> = (props) => (
  <FmResponsive>
    {(tier) => (
      <div
        data-testid={
          tier === "desktop" ? undefined : "forest-stand-filters-sheet"
        }
      >
        <FilterPanelContent {...props} />
      </div>
    )}
  </FmResponsive>
);

const CollectionPanel: React.FC<PanelProps> = ({
  panel,
  onAction,
  onCollectionSelect,
}) => {
  if (panel.kind !== "collection") return null;
  const primaryAction = panel.primaryAction;
  return (
    <FmStack gap="md" data-testid={`map-collection-${panel.id}`}>
      {primaryAction ? (
        <FmButton
          variant="primary"
          disabled={Boolean(primaryAction.disabledReason)}
          title={primaryAction.disabledReason}
          onClick={() => onAction(primaryAction.id)}
          data-testid={`map-collection-${panel.id}-primary-action`}
        >
          {primaryAction.label}
        </FmButton>
      ) : null}
      {panel.actions.length ? (
        <FmRowActions
          actions={panel.actions.map((action) => ({
            id: action.id,
            label: action.label,
            intent: action.intent,
            disabledReason: action.disabledReason,
            confirm: action.confirm,
            onSelect: () => onAction(action.id),
          }))}
          policy="primary-plus-menu"
          label={panel.actionLabel}
          overflowLabel={panel.overflowLabel}
          data-testid={`map-collection-${panel.id}-actions`}
        />
      ) : null}
      {panel.records.length === 0 ? (
        <FmEmpty description={panel.emptyLabel} />
      ) : (
        <ol className="fm-map-collection">
          {panel.records.map((record) => {
            const selected = panel.selectedIds.includes(record.id);
            return (
              <li
                key={record.id}
                className="fm-map-collection__record"
                data-selected={String(selected)}
              >
                <button
                  type="button"
                  className="fm-map-collection__select"
                  aria-current={selected ? "true" : undefined}
                  onClick={() => onCollectionSelect(panel.id, record.id)}
                  data-testid={`map-collection-record-${record.id}`}
                >
                  <FmCellStack
                    primary={record.title}
                    secondary={record.description}
                    metaItems={record.metadata.map((item) => ({
                      id: item.id,
                      label: item.label,
                    }))}
                    footer={
                      record.status ? (
                        <FmChip variant={selected ? "brand" : "mute"}>
                          {record.status}
                        </FmChip>
                      ) : undefined
                    }
                  />
                </button>
                <FmRowActions
                  actions={record.actions.map((action) => ({
                    id: action.id,
                    label: action.label,
                    intent: action.intent,
                    disabledReason: action.disabledReason,
                    confirm: action.confirm,
                    onSelect: () => onAction(`${action.id}:${record.id}`),
                  }))}
                  policy="primary-plus-menu"
                  label={panel.actionLabel}
                  overflowLabel={panel.overflowLabel}
                />
              </li>
            );
          })}
        </ol>
      )}
    </FmStack>
  );
};

export const MapWorkspacePanel: React.FC<PanelProps> = (props) => {
  const { panel, layers } = props;
  if (panel.kind === "legend") {
    return (
      <MapWorkspaceLegend
        panel={panel}
        layers={layers}
        baseMaps={props.baseMaps}
        activeBaseMapId={props.activeBaseMapId}
        onLayerToggle={props.onLayerToggle}
        onBaseMapSelect={props.onBaseMapSelect}
        onAction={props.onAction}
      />
    );
  }
  if (panel.kind === "filters") return <FilterPanel {...props} />;
  if (panel.kind === "collection") return <CollectionPanel {...props} />;
  if (panel.kind === "section")
    return <FmSection section={panel.section} presentation="embedded" headingLevel={3} />;
  return null;
};
